import React, { useId, useState } from 'react';
import { cn } from '@/lib/utils';
import { PUBLISHED_SLOT_TIERS } from '@/lib/publishedSlotTiers';
import { formatCents } from '@/lib/slotPricing';
import Button from '../Button';
import TierCoaster from './TierCoaster';
import { formatIndex } from '../Eyebrow';
import { preloadEnquiryDialog, useEnquiryDialog } from '../enquiry/EnquiryProvider';
import usePricingTiers from './usePricingTiers';
import {
  annualSavingCents,
  MAX_VENUES,
  MIN_VENUES,
  PLAN_COPY,
  annualFreeText,
  calculatorSummary,
  clampVenues,
  paidTiers,
  planContextFor,
  planFigures,
  pricingNote,
} from './pricingModel';

// Card surfaces (the package's .price-card / .premium / .sponsor). Premium is an orange
// card with navy text (4.62:1); its focus ring is navy (rust and peach fail on orange).
const CARD_TONE = {
  standard: 'site-light border-navy/20 bg-brand-sheet text-navy',
  premium: 'border-navy/20 bg-brand-orange text-navy [--focus-ring:theme(colors.navy.DEFAULT)] nav:py-[42px] xl:py-12',
  sponsor: 'site-dark border-navy bg-navy text-brand-sheet',
};
// The package's raised Premium card (`.price-card.premium`: translateY(-14px),
// margin-bottom -28px, above its neighbours). On the grid item (the <li>), not the card: the
// item then grows 28px and moves up 14px, so the card sticks out 14px above and below the
// others and, with its 14px more padding, its "Choose" button lines up with theirs.
const ITEM_OFFSET = { premium: 'nav:z-[2] nav:-mb-7 nav:-translate-y-3.5' };

// Plan name and "Choose {Tier}" button sizes. Home: the package's .price-card h3 (35px phones,
// 27px from 800px, 31px from 1280px) and 12px buttons from 800px. Page: pages.css's
// `.page-main h3` (28px phones, clamp(27px, 2.5vw, 36px) from 800px) and `.page-main
// .button` (52px tall, 15px). The size comes before the leading (tailwind-merge).
const PLAN_NAME_SIZE = {
  home: 'text-[2.1875rem] nav:text-[1.6875rem] xl:text-[1.9375rem]',
  page: 'text-[1.75rem] nav:text-[clamp(1.6875rem,2.5vw,2.25rem)]',
};
const CHOOSE_SIZE = {
  home: 'text-sm leading-tight nav:text-xs',
  page: 'min-h-[3.25rem] text-[0.9375rem] leading-tight',
};
const SOFT_TEXT = { standard: 'text-brand-muted', premium: 'text-navy', sponsor: 'text-brand-sheet/80' };
const DASHED = { standard: 'border-navy/25', premium: 'border-navy/30', sponsor: 'border-brand-sheet/30' };
const FALLBACK_NAMES = { standard: 'Standard', premium: 'Premium', sponsor: 'Venue Sponsor' };
// An inline-size container: the price below is sized from the card's width (cqi).
const CARD_BASE = 'relative flex h-full w-full flex-col border px-[27px] py-[30px] shadow-[5px_6px_0] shadow-navy/10 [container-type:inline-size] nav:px-[21px] nav:py-7 xl:px-7 xl:py-[34px]';

function PlanCard({ tier, index, interval, venues, headingLevel, variant, onChoose, chooseOpensDialog }) {
  const headingId = useId();
  const key = tier.tier_key;
  const copy = PLAN_COPY[key];
  const f = planFigures(tier, interval, venues);
  const Heading = `h${headingLevel}`;
  const forVenues = `for ${f.venues} ${f.venues === 1 ? 'venue' : 'venues'}`;

  // The design's lines: "$3,636.90 billed quarterly" / "3-month minimum" (Sponsor:
  // "12-month commitment · $Z total"), plus the discount rule that applies.
  const billLines = [];
  if (f.interval === 'annual') {
    billLines.push(`${formatCents(f.billCents)} billed yearly`);
    // Upgrade brief §3.5: the saving in dollars, against quarterly billing for the same venues.
    const saving = annualSavingCents(tier, venues);
    if (saving > 0) billLines.push(`Save ${formatCents(saving)} a year`);
    billLines.push(f.monthsFree ? `Includes ${f.monthsFree === 1 ? '1 month' : `${f.monthsFree} months`} free` : 'Paid once a year');
    if (f.venues > 1) billLines.push('No multi-venue discount on annual prepay');
  } else {
    billLines.push(`${formatCents(f.billCents)} billed quarterly`);
    if (key === 'sponsor') {
      billLines.push(`${f.quarterlyOnly ? 'Quarterly only · ' : ''}12-month commitment · ${formatCents(f.commitmentTotalCents)} total`);
    } else {
      billLines.push(f.quarterlyOnly ? 'Quarterly only · 3-month minimum' : '3-month minimum');
    }
    if (f.pct > 0) billLines.push(`Includes ${f.pct}% multi-venue discount`);
  }

  return (
    <article aria-labelledby={headingId} className={cn(CARD_BASE, CARD_TONE[key])} data-plan={key}>
      {/* The mono label at 12px, never smaller (upgrade brief §2.6); factual, never "Most popular". */}
      {/* Upgrade brief §3.5: each tier shows its physical placement — the example coaster. */}
      <TierCoaster tier={tier} sizes="(min-width: 1808px) 520px, (min-width: 800px) 30vw, 100vw" />
      <p className="m-0 font-label text-xs uppercase tracking-[0.01em]" data-plan-label={key}>
        {formatIndex(index)} <span aria-hidden="true">/</span> {copy.label}
      </p>
      <Heading
        id={headingId}
        className={cn(
          // With enlarged text on a narrow phone a plan name longer than the card breaks
          // inside the card (hyphenated under 20em, anywhere as the last resort) instead of
          // running past it (and the page's edge).
          'mb-1.5 mt-5 font-display font-medium [overflow-wrap:anywhere] [@media(max-width:19.9375em)]:hyphens-auto',
          PLAN_NAME_SIZE[variant] || PLAN_NAME_SIZE.home,
          'leading-[1.12] tracking-[-0.015em]',
        )}
      >
        {tier.name || FALLBACK_NAMES[key]}
      </Heading>
      <p className={cn('mb-[26px] mt-0 text-sm nav:text-[0.8125rem]', SOFT_TEXT[key])}>{copy.description}</p>

      <p className="m-0">
        {/* One line, never broken ("$1,99/9"): the package's 48 / 40 / 46px, but never more
            than 21% of the card's width (cqi), so the longest total the calculator can show
            ("$14,983.33", 4.4em wide) still fits when the text is enlarged. */}
        <span
          data-price={key}
          className="block whitespace-nowrap font-numeral text-[min(3rem,21cqi)] font-medium leading-none tracking-[-0.065em] nav:text-[min(2.5rem,21cqi)] xl:text-[min(2.875rem,21cqi)]"
        >
          {formatCents(f.monthlyTotalCents)}
        </span>
        <span className="mt-[9px] block text-[0.8125rem] nav:text-xs" data-price-basis={key}>
          {`per month, ${forVenues}${f.interval === 'annual' ? ', paid yearly' : ''}`}
        </span>
      </p>

      <div
        className={cn(
          'mt-3 border-b border-dashed pb-[18px] text-[0.8125rem] leading-[1.8] nav:min-h-[68px]',
          variant === 'page' ? 'nav:text-sm' : 'nav:text-xs',
          DASHED[key],
        )}
        data-bill={key}
      >
        {billLines.map((line) => (
          <p key={line} className="m-0">{line}</p>
        ))}
      </div>

      <ul className={cn('mb-6 mt-2.5 flex-1 list-none p-0', variant === 'page' ? 'text-[0.9375rem]' : 'text-sm nav:text-[0.8125rem]')}>
        {copy.lines(tier).map((line) => (
          <li key={line} className={cn('relative border-b border-dashed py-3.5 pl-[19px]', DASHED[key])}>
            <span aria-hidden="true" className="absolute left-0">✓</span>
            {line}
          </li>
        ))}
      </ul>

      <Button
        variant="secondary"
        size="sm"
        fullWidth
        className={CHOOSE_SIZE[variant] || CHOOSE_SIZE.home}
        aria-haspopup={chooseOpensDialog ? 'dialog' : undefined}
        onPointerEnter={chooseOpensDialog ? preloadEnquiryDialog : undefined}
        onFocus={chooseOpensDialog ? preloadEnquiryDialog : undefined}
        onClick={() => onChoose(tier)}
      >
        {copy.choose}
      </Button>
    </article>
  );
}

function SkeletonCard({ index }) {
  const tone = ['standard', 'premium', 'sponsor'][index] || 'standard';
  const bar = tone === 'sponsor' ? 'bg-brand-sheet/15' : 'bg-navy/10';
  return (
    <div aria-hidden="true" className={cn(CARD_BASE, CARD_TONE[tone], 'min-h-[35rem]')} data-plan-skeleton="">
      <div className={cn('h-3 w-32 motion-safe:animate-pulse', bar)} />
      <div className={cn('mt-6 h-8 w-40 motion-safe:animate-pulse', bar)} />
      <div className={cn('mt-3 h-4 w-36 motion-safe:animate-pulse', bar)} />
      <div className={cn('mt-8 h-12 w-32 motion-safe:animate-pulse', bar)} />
      <div className={cn('mt-5 h-10 w-full motion-safe:animate-pulse', bar)} />
      <div className={cn('mt-6 h-28 w-full flex-1 motion-safe:animate-pulse', bar)} />
      <div className={cn('mt-6 h-11 w-full motion-safe:animate-pulse', bar)} />
    </div>
  );
}

/**
 * The pricing calculator (the package's #pricing tools + plan cards + note): billing
 * interval, number of venues (1–20), and the Standard / Premium / Venue Sponsor cards with
 * live prices from GET /api/slots/tiers (usePricingTiers; the published list prices when
 * the catalogue is not published) and the maths of lib/slotPricing. The section heading
 * ("Small format. Clear commitment.") and the free-listing strip belong to the page.
 *
 * Props:
 * - `variant`: 'home' (default: plan names are h3 under the section's h2) | 'page' (the
 *   Pricing page: plan names are h2 under the page's h1, at the interior h3 size; larger
 *   card text; 52px "Choose {Tier}" buttons)
 * - `pricing`: optional `{ tiers, status, retry }`, the result of the page's own
 *   usePricingTiers() — the calculator then makes no request of its own (one GET per page,
 *   one set of prices on screen), and a retry keeps the visitor's interval and venue
 *   count. The page then owns the error: the calculator's error box is not a live region
 *   (the page announces it) and its button is "Reload prices" (the page's retry). Pass it
 *   on every render or never (switching remounts the calculator). Without it (home) the
 *   calculator loads the tiers itself, announces an error and offers "Try again".
 * - `defaultInterval`: 'quarterly' (default) | 'annual'
 * - `defaultVenues`: 1 (default), clamped to 1–20
 * - `onChoose(tierKey, planContext)`: optional; default: "Choose {Tier}" opens the enquiry
 *   dialog with that tier pre-selected, planContext { tier, interval, venue_count }
 *   (Sponsor's interval is always quarterly)
 * - `className`
 *
 * Accessibility: the interval is a group of two aria-pressed buttons; the stepper is a
 * labelled number input between "One fewer venue" / "One more venue" buttons; the summary
 * next to it is a polite live region; each card is an article named by its plan heading.
 */
export default function PricingCalculator({ pricing, ...props }) {
  return pricing ? <Calculator {...props} pricing={pricing} pageOwned /> : <SelfLoadingCalculator {...props} />;
}

function SelfLoadingCalculator(props) {
  const pricing = usePricingTiers();
  return <Calculator {...props} pricing={pricing} pageOwned={false} />;
}

function Calculator({
  variant = 'home',
  defaultInterval = 'quarterly',
  defaultVenues = 1,
  onChoose,
  className,
  pricing,
  pageOwned,
}) {
  const uid = useId();
  const [interval, setInterval] = useState(defaultInterval === 'annual' ? 'annual' : 'quarterly');
  const [venues, setVenues] = useState(() => clampVenues(defaultVenues));
  const [draft, setDraft] = useState(() => String(clampVenues(defaultVenues)));
  const { tiers = [], status = 'loading', retry } = pricing || {};
  const enquiry = useEnquiryDialog();

  const shown = paidTiers(tiers);
  const labelTiers = shown.length ? tiers : PUBLISHED_SLOT_TIERS;
  const freeText = annualFreeText(labelTiers);
  const headingLevel = variant === 'page' ? 2 : 3;
  const inputId = `${uid}-venues`;
  const summaryId = `${uid}-summary`;

  const commit = (n) => {
    const v = clampVenues(n);
    setVenues(v);
    setDraft(String(v));
  };

  const onDraftChange = (e) => {
    const raw = e.target.value;
    setDraft(raw);
    const n = Number(raw);
    if (raw !== '' && Number.isInteger(n) && n >= MIN_VENUES && n <= MAX_VENUES) setVenues(n);
  };

  const choose = (tier) => {
    const ctx = planContextFor(tier, interval, venues);
    if (onChoose) onChoose(tier.tier_key, ctx);
    else enquiry.open({ role: 'advertiser', planContext: ctx });
  };

  return (
    <div className={cn('site-light text-navy', className)} data-pricing-calculator={variant}>
      <div className="mb-[30px] flex flex-wrap items-start justify-between gap-6 nav:mb-11 nav:items-center nav:gap-5">
        <div role="group" aria-label="Billing frequency" className="flex w-full border border-navy/30 bg-brand-rule/70 p-1 nav:w-auto">
          {[
            { key: 'quarterly', label: 'Quarterly' },
            { key: 'annual', label: 'Annual', note: freeText },
          ].map((opt) => {
            const on = interval === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                aria-pressed={on}
                onClick={() => setInterval(opt.key)}
                className={cn(
                  'min-h-11 flex-1 px-[17px] py-2.5 font-body text-sm leading-tight transition-colors duration-180 nav:flex-none nav:text-[0.8125rem]',
                  on ? 'bg-navy text-brand-sheet' : 'bg-transparent text-navy hover:bg-brand-sheet/60',
                )}
              >
                {opt.label}
                {opt.note ? (
                  <span className="mt-0.5 block text-xs">
                    {opt.note}
                    <span aria-hidden="true">*</span>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="flex w-full flex-wrap items-center gap-3 nav:w-auto nav:gap-3.5">
          <label htmlFor={inputId} className="text-[0.8125rem]">How many venues?</label>
          <div className="flex border border-navy/30 bg-brand-sheet">
            <button
              type="button"
              aria-label="One fewer venue"
              aria-controls={inputId}
              disabled={venues <= MIN_VENUES}
              onClick={() => commit(venues - 1)}
              className="min-h-11 w-9 bg-transparent font-body text-xl leading-none text-navy disabled:opacity-40"
            >
              <span aria-hidden="true">−</span>
            </button>
            <input
              id={inputId}
              type="number"
              inputMode="numeric"
              min={MIN_VENUES}
              max={MAX_VENUES}
              step={1}
              value={draft}
              onChange={onDraftChange}
              onBlur={() => commit(draft === '' ? venues : draft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit(draft === '' ? venues : draft);
                }
              }}
              aria-describedby={summaryId}
              className="w-10 border-0 bg-transparent text-center font-label text-sm text-navy [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
              type="button"
              aria-label="One more venue"
              aria-controls={inputId}
              disabled={venues >= MAX_VENUES}
              onClick={() => commit(venues + 1)}
              className="min-h-11 w-9 bg-transparent font-body text-xl leading-none text-navy disabled:opacity-40"
            >
              <span aria-hidden="true">+</span>
            </button>
          </div>
          {/* Billing terms: 14px at full strength, like the note below (upgrade brief §2.6). */}
          <p id={summaryId} aria-live="polite" className="m-0 min-w-[94px] basis-full text-sm leading-snug nav:basis-auto" data-pricing-summary="">
            {calculatorSummary(interval, venues, labelTiers)}
          </p>
        </div>
      </div>

      {status === 'error' ? (
        // Self-loading (home): a status region with its own "Try again". Page-owned: the
        // page's alert announces the error, so this box is plain text, and its button has
        // its own name next to the page's "Try again" (the same retry, one request).
        <div
          role={pageOwned ? undefined : 'status'}
          className="border border-navy/20 bg-brand-sheet p-6 text-center nav:p-10"
          data-pricing-error=""
        >
          <p className="m-0 text-base">We couldn&apos;t load the prices just now.</p>
          {typeof retry === 'function' ? (
            <Button variant="secondary" arrow={'↺'} className="mt-5" onClick={retry}>
              {pageOwned ? 'Reload prices' : 'Try again'}
            </Button>
          ) : null}
        </div>
      ) : null}

      {status === 'loading' && !shown.length ? (
        <div role="status" data-pricing-loading="">
          <span className="sr-only">Loading prices…</span>
          <div className="grid grid-cols-1 gap-5 nav:grid-cols-3 nav:gap-0">
            {[0, 1, 2].map((i) => <SkeletonCard key={i} index={i} />)}
          </div>
        </div>
      ) : null}

      {status !== 'error' && shown.length ? (
        <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 nav:grid-cols-3 nav:items-stretch nav:gap-0" aria-busy={status === 'loading' || undefined}>
          {shown.map((tier, i) => (
            <li key={tier.tier_key} className={cn('flex', ITEM_OFFSET[tier.tier_key])}>
              <PlanCard
                tier={tier}
                index={i + 1}
                interval={interval}
                venues={venues}
                headingLevel={headingLevel}
                variant={variant}
                onChoose={choose}
                chooseOpensDialog={!onChoose}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {/* The billing footnote: 14px, full-strength navy (upgrade brief §2.6: buyers read these terms). */}
      <p className="mx-auto mb-0 mt-[30px] max-w-[780px] text-sm leading-relaxed text-navy nav:mt-[54px] nav:text-center" data-pricing-note="">
        {pricingNote(interval, labelTiers)}
      </p>
      {enquiry.dialog}
    </div>
  );
}
